<template>
  <div
    id="ticket-report-modal"
    ref="modal"
    class="modal fade sp-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="ticket-report-title"
    aria-hidden="true"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 id="ticket-report-title" class="modal-title">Generate a report</h5>
            <p class="sp-modal-subtitle">
              Tickets filed in a date range: totals, a breakdown by service type and unit, and
              the list of tickets.
            </p>
          </div>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div class="sp-label">Period</div>
          <div class="sp-period-chips" role="group" aria-label="Quick periods">
            <button
              v-for="p in periods"
              :key="p.key"
              type="button"
              class="sp-period-chip"
              :class="{ on: period === p.key }"
              :aria-pressed="period === p.key ? 'true' : 'false'"
              @click="setPeriod(p.key)"
            >
              {{ p.label }}
            </button>
          </div>
          <div class="sp-date-range mt-2">
            <input
              v-model="from"
              type="date"
              class="form-control"
              aria-label="Filed from"
              :max="to || null"
              @input="period = 'custom'"
            />
            <span>to</span>
            <input
              v-model="to"
              type="date"
              class="form-control"
              aria-label="Filed until"
              :min="from || null"
              @input="period = 'custom'"
            />
          </div>
          <div v-if="rangeError" class="sp-field-error">{{ rangeError }}</div>

          <div class="sp-report-grid">
            <div>
              <label for="report-unit" class="sp-label">Unit/Section</label>
              <select id="report-unit" v-model="unit" class="form-control">
                <option value="">All units</option>
                <option v-for="u in unitOptions" :key="u.value" :value="u.value">
                  {{ unitLabel(u.value) }}
                </option>
              </select>
            </div>
            <div>
              <label for="report-type" class="sp-label">Service type</label>
              <select id="report-type" v-model="type" class="form-control">
                <option value="">All types</option>
                <option v-for="t in typeOptions" :key="t.value" :value="t.value">{{ t.value }}</option>
              </select>
            </div>
            <div>
              <label for="report-status" class="sp-label">Status</label>
              <select id="report-status" v-model="status" class="form-control">
                <option v-for="s in statusOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
              </select>
            </div>
          </div>

          <div class="sp-report-preview" aria-live="polite">
            <i class="bi bi-info-circle" aria-hidden="true"></i>
            <span v-if="rangeError">Choose a valid period to see how many tickets it covers.</span>
            <span v-else-if="previewLoading">Counting tickets…</span>
            <span v-else-if="preview !== null">
              <strong>{{ preview }}</strong> {{ preview === 1 ? "ticket matches" : "tickets match" }}.
              <template v-if="!preview">The report will say no tickets were filed.</template>
            </span>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="!!rangeError || !!busy"
            @click="generate('csv')"
          >
            <span v-if="busy === 'csv'" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-file-earmark-spreadsheet mr-1"></i>
            Excel (CSV)
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!!rangeError || !!busy"
            @click="generate('pdf')"
          >
            <span v-if="busy === 'pdf'" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-file-earmark-pdf mr-1"></i>
            PDF
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PERIODS, errorMessage, periodRange, unitLabel } from "../../supportOptions";

const STATUS_OPTIONS = [
  { key: "all", label: "All statuses" },
  { key: "open", label: "Open (not closed)" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In progress" },
  { key: "on_hold", label: "On hold" },
  { key: "forwarded", label: "With GSS" },
  { key: "closed", label: "Closed" },
];

// Errors come back as a Blob because the request asks for a file.
async function blobErrorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const first = parsed.errors && Object.values(parsed.errors)[0];
      return (first && first[0]) || parsed.message || fallback;
    } catch (e) {
      return fallback;
    }
  }
  return errorMessage(err, fallback);
}

/** Support desk report for a date range, as a PDF or a CSV for Excel. */
export default {
  name: "TicketReportModal",
  props: {
    // { types: [{ value, count }], units: [...] } from the ticket list.
    facets: { type: Object, default: () => ({ types: [], units: [] }) },
  },
  data() {
    return {
      periods: [...PERIODS, { key: "custom", label: "Custom" }],
      statusOptions: STATUS_OPTIONS,
      period: "this_month",
      from: "",
      to: "",
      unit: "",
      type: "",
      status: "all",
      preview: null,
      previewLoading: false,
      busy: "",
    };
  },
  computed: {
    rangeError() {
      if (!this.from || !this.to) return "Choose a start and end date.";
      if (this.from > this.to) return "The start date is after the end date.";
      return "";
    },
    unitOptions() {
      return this.withSelected(this.facets.units || [], this.unit);
    },
    typeOptions() {
      return this.withSelected(this.facets.types || [], this.type);
    },
    previewKey() {
      return [this.from, this.to, this.unit, this.type, this.status].join("|");
    },
  },
  watch: {
    previewKey() {
      this.queuePreview();
    },
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer);
    $(this.$refs.modal).modal("hide");
  },
  methods: {
    unitLabel,
    /** Opens with the list's current filters, or this month. */
    open(defaults = {}) {
      this.unit = defaults.unit || "";
      this.type = defaults.type || "";
      this.status = STATUS_OPTIONS.some((s) => s.key === defaults.status) ? defaults.status : "all";
      if (defaults.from || defaults.to) {
        this.period = "custom";
        this.from = defaults.from || defaults.to;
        this.to = defaults.to || defaults.from;
      } else {
        this.setPeriod("this_month");
      }
      this.busy = "";
      this.queuePreview(0);
      $(this.$refs.modal).modal("show");
    },
    setPeriod(key) {
      this.period = key;
      const range = periodRange(key);
      if (range) [this.from, this.to] = range;
    },
    withSelected(options, selected) {
      if (!selected || options.some((o) => o.value === selected)) return options;
      return [{ value: selected, count: 0 }, ...options];
    },
    params(format) {
      return {
        from: this.from,
        to: this.to,
        unit: this.unit || undefined,
        type: this.type || undefined,
        status: this.status,
        format,
      };
    },
    queuePreview(delay = 300) {
      clearTimeout(this.previewTimer);
      if (this.rangeError) {
        this.preview = null;
        return;
      }
      this.previewTimer = setTimeout(this.loadPreview, delay);
    },
    async loadPreview() {
      const ticket = (this.previewTicket = (this.previewTicket || 0) + 1);
      this.previewLoading = true;
      try {
        // The list counts the same tickets the report covers.
        const { data } = await axios.get("api/tickets", {
          params: { ...this.params(), format: undefined, scope: "all", perPage: 5 },
        });
        if (ticket === this.previewTicket) this.preview = data.total || 0;
      } catch (e) {
        if (ticket === this.previewTicket) this.preview = null;
      } finally {
        if (ticket === this.previewTicket) this.previewLoading = false;
      }
    },
    async generate(format) {
      if (this.rangeError || this.busy) return;
      // The PDF opens in a new tab: open it now so pop-up blockers allow it.
      const win = format === "pdf" ? window.open("", "_blank") : null;
      if (win) {
        win.document.write('<p style="font-family:sans-serif;padding:24px;color:#555">Preparing the report…</p>');
        win.document.close();
      }

      this.busy = format;
      try {
        const res = await axios.get("api/support/tickets/report", {
          params: this.params(format),
          responseType: "blob",
        });
        const type = format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";
        const url = URL.createObjectURL(new Blob([res.data], { type }));
        if (win && !win.closed) {
          win.location.href = url;
        } else {
          const match = (res.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/i);
          const link = document.createElement("a");
          link.href = url;
          link.download = match ? match[1] : `IRMS_Report_${this.from}_to_${this.to}.${format}`;
          link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 120000);
        toastr.success(format === "pdf" ? "Report ready." : "Report downloaded.", "Report");
      } catch (err) {
        if (win && !win.closed) win.close();
        toastr.error(await blobErrorMessage(err, "The report couldn't be generated."), "Report");
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>
